import axios from 'axios'
import { apiConfig } from './env'
import router from '@/router' // 补充引入router

// 创建axios实例
const api = axios.create(apiConfig)

// 请求拦截器
api.interceptors.request.use(
  config => {
    // 在发送请求之前做些什么
    const token = localStorage.getItem('authToken')
    if (token) {
      // config.headers.Authorization = `Bearer ${token}`
      config.headers['token'] = token
    }
    
    // 添加CORS绕过头（仅用于开发环境）
    if (process.env.NODE_ENV === 'development') {
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
    }
    
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器
api.interceptors.response.use(
  response => {
    // 对响应数据做点什么
    return response
  },
  error => {
    // 对响应错误做点什么
    console.error('API Response Error:', error.response?.status, error.response?.data)
    
    // 处理401未授权错误
    if (error.response?.status === 401) {
      // 获取当前路由
      const currentRoute = router.currentRoute
      
      // 定义不需要登录验证的页面白名单
      const publicRoutes = [
        '/ISACI2025',
        '/meetings/registration',
        '/login',
        '/',
        '/create-account',
        '/forgot-password'
      ]
      
      // 获取当前路径（Vue Router 在 hash 模式下，path 已经处理了 hash）
      const currentPath = currentRoute.path || ''
      
      // 调试信息（开发环境）
      if (process.env.NODE_ENV === 'development') {
        console.log('[API Interceptor] 401 Error - Current path:', currentPath)
        console.log('[API Interceptor] Public routes:', publicRoutes)
      }
      
      // 检查当前路由是否在白名单中
      const isPublicRoute = publicRoutes.some(route => {
        // 精确匹配
        if (currentPath === route) {
          if (process.env.NODE_ENV === 'development') {
            console.log('[API Interceptor] Matched public route (exact):', route)
          }
          return true
        }
        // 路径前缀匹配（但不包括完全相同的路径）
        if (route !== '/' && currentPath.startsWith(route + '/')) {
          if (process.env.NODE_ENV === 'development') {
            console.log('[API Interceptor] Matched public route (prefix):', route)
          }
          return true
        }
        // 根路径匹配
        if (route === '/' && currentPath === '/') {
          if (process.env.NODE_ENV === 'development') {
            console.log('[API Interceptor] Matched public route (root):', route)
          }
          return true
        }
        return false
      })
      
      // 如果不在白名单中，才跳转到登录页面
      if (!isPublicRoute) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[API Interceptor] Redirecting to login page')
        }
        localStorage.removeItem('authToken')
        router.push('/login') // 用router实例跳转
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('[API Interceptor] Public route, skipping login redirect')
        }
      }
    }
    
    return Promise.reject(error)
  }
)

// API方法
export const apiService = {
  // 用户认证相关API
  auth: {
    // 用户登录 - 使用正确的接口路径和参数
    login: (credentials) => api.post('/api/user/login', {
      account: credentials.email || credentials.account,
      password: credentials.password
    }),
    // 邮箱验证 - 检查邮箱是否已被注册
    checkEmail: (username) => api.post('/api/user/isRegister', { username }),
    // 用户注册
    register: (userData) => api.post('/api/user/register', userData),
    // 用户登出
    logout: () => api.post('/api/user/logout'),
    // 获取用户信息
    getProfile: () => api.get('/api/user/userInfo'),
    // 更新用户信息
    updateProfile: (data) => api.put('/api/user/profile', data),
    // 忘记密码
    forgotPassword: (data) => api.post('/api/v2.home/send_email', data),
    // 重置密码
    resetPassword: (data) => api.post('/api/v2.home/doResetPwd', data),
    // 修改密码
    changePassword: (data) => api.post('/api/user/updatePwd', data),
    // 通过邮箱查找用户信息
    getUserByEmail: (username) => api.post('/api/user/userInfo', { username }),
    // 联系我们
    getContactInfo: (data) => api.post('/api/v2.message/save', data)
  },
  
  // 摘要相关API
  abstracts: {
    // 获取摘要列表
    getList: (params) => api.get('/api/user/myAbstract', { params }),
    // 获取单个摘要
    getById: (id) => api.get(`/api/abstracts/${id}`),
    // 创建摘要
    create: (data) => api.post('/api/user/addAbstract', data),
    // 更新摘要
    update: (data) => api.put('/api/user/addAbstract', data),
    // 删除摘要
    delete: (data) => api.delete(`/api/user/delAbstract`, { data }),
    // 创建摘要支付账单订单号
    createAbstractBill: (data) => api.post('/api/v2.pay/createAbstractOrder', data),
    // 订单号支付
    payAbstractBill: (data) => api.post('/api/v2.pay/payAbstractOrder', data)
  },
  
  // 会议相关API
  sessions: {
    // 注册会议
    register: (data) => api.post('/api/v2.home/registration', data),
    // 获取会议列表
    getList: (params) => api.get('/api/user/sessionList', { params }),
    // 获取正在进行的会议列表
    getActiveList: (params) => api.get('/api/v2.session/index', { params }),
    // 注册票和订单号生成
    getRegistrationSlip: (params) => api.get('/api/v2.registration_ticket/generate', { params }),
    // 获取注册信息
    getRegistrationInfo: () => api.get('/api/v2.user/list'),
    // 获取会议的票价列表
    getTicketList: (params) => api.get('/api/v2.registration_ticket/price_list', { params }),
    // 查询会议列表（用于摘要提交时选择）
    getSessionList: (params) => api.get('/api/user/sessionList', { params }),
    // 获取会议详情
    getSessionDetails: (params) => api.get('/api/v2.sessioninfo/detail', { params }),
    // 获取获取个人注册会议列表
    getMySessionList: (params) => api.get('/api/v2.attendance/sessions', { params }),
    // 创建注册支付账单订单号
   createRegistrationBill : (data) => api.post('/api/v2.registration_ticket/generate', data),
    // 支付订单
    doPay: (data) => api.post('/api/v2.pay/doPay', data)
  },
  
  // 公告相关API
  announcements: {
    // 获取公告列表
    getList: (params) => api.get('/api/user/announcementList', { params }),
    // 获取单个公告
    getById: (id) => api.get(`/api/announcements/${id}`)
  },
  
  // 注册相关API
  registration: {
    // 获取注册信息
    getInfo: () => api.get('/api/registration/info'),
    // 提交注册
    submit: (data) => api.post('/api/registration/submit', data),
    // 获取注册票据
    getSlip: () => api.get('/api/registration/slip'),
    // 获取出席证明
    getAttendance: (params) => api.get('/api/v2.attendance/index', { params }),
    // 获取发票列表
    getInvoiceList: (params) => api.get('/api/v2.orders/order_list', { params })
  },
  
  // 发票相关API
  invoices: {
    // 获取发票列表
    getList: (params) => api.get('/api/v2.orders/order_list', { params }),
    // 获取单个发票
    getById: (id) => api.get(`/api/invoices/${id}`),
    // 下载发票
    download: (id) => api.get(`/api/invoices/${id}/download`, { responseType: 'blob' })
  },
  // 会议日程相关API
  schedule: {
    // 获取会议日程 

    getList: (params) => api.get('/api/v2.schedule/index', { params })
  }
}

// export { apiService }
export default api 